import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const ConfirmClient = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const [resetLink, setResetLink] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Missing confirmation token.");
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("confirm-pending-client", {
          body: { token },
        });
        if (error) throw error;
        if ((data as any)?.error) throw new Error((data as any).error);
        setResetLink((data as any)?.reset_link || null);
        setState("success");
      } catch (err: any) {
        setState("error");
        setMessage(err?.message || "Confirmation failed.");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="max-w-md w-full text-center space-y-6 border border-border/50 bg-card p-10">
        <p className="text-primary text-xs font-light tracking-[0.5em] uppercase">In Paradise Beauty</p>
        {state === "loading" && (
          <>
            <Loader2 className="mx-auto animate-spin text-primary" size={40} />
            <h1 className="font-serif text-2xl italic">Confirming your account…</h1>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-primary" size={48} />
            <h1 className="font-serif text-2xl italic">You're all set</h1>
            <p className="text-sm text-muted-foreground font-light leading-relaxed">
              Your account has been confirmed. {resetLink ? "Click below to set your password and log in." : "Use \"Forgot Password\" on the login page to set your password."}
            </p>
            {resetLink ? (
              <Button asChild className="w-full text-xs tracking-widest uppercase">
                <a href={resetLink}>Set Password</a>
              </Button>
            ) : (
              <Button asChild className="w-full text-xs tracking-widest uppercase">
                <Link to="/auth">Go to Login</Link>
              </Button>
            )}
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="mx-auto text-destructive" size={48} />
            <h1 className="font-serif text-2xl italic">Unable to confirm</h1>
            <p className="text-sm text-muted-foreground font-light leading-relaxed">{message}</p>
            <Button asChild variant="outline" className="w-full text-xs tracking-widest uppercase">
              <Link to="/">Return Home</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmClient;
